import type { ChatFileInfo } from "@nexus/types"
import { createAsyncThunk } from "@reduxjs/toolkit"

import {
  createChatRoom,
  type EmbedRequestFailure,
  sendMessageToChat,
  toEmbedRequestFailure
} from "@/common/services/chat"

import type { EmbedRootState } from "../embedSelectors"

export interface SendEmbedMessageParams {
  chatId: string
  content: string
  messageId: string
  deploymentId: string
  sessionId: string
  agentId: string
  knowledgeIds?: string[]
  images?: string[]
  /** File info for optimistic display */
  files?: ChatFileInfo[]
  /** Customer identity data for embed sessions */
  embedData?: Record<string, string>
}

export interface SendEmbedMessageResult {
  chatId: string
  messageId: string
  content: string
  wasNewChatroom: boolean
}

/**
 * Send message thunk - Embed only
 * Uses integration headers (no auth token)
 *
 * For new chatrooms (isNewChatroom: true), creates the chat + first message.
 * For existing chatrooms, sends message to existing chat.
 *
 * ONE attempt. A refusal rejects with its envelope code intact, so a caller can
 * tell a closed conversation from any other failure; `deliverEmbedMessage` is the
 * caller that acts on it.
 */
export const sendEmbedMessage = createAsyncThunk<
  SendEmbedMessageResult,
  SendEmbedMessageParams,
  { state: EmbedRootState; rejectValue: EmbedRequestFailure }
>(
  "embedChat/sendMessage",
  async (
    {
      chatId,
      content,
      messageId,
      deploymentId,
      sessionId,
      agentId,
      knowledgeIds = [],
      images = [],
      embedData
    },
    { rejectWithValue, getState }
  ) => {
    try {
      const chat = getState().embedChat.chats[chatId]
      const isNewChatroom = chat?.isNewChatroom ?? false
      const shouldGenerateTopic = !chat?.topic

      if (isNewChatroom) {
        // First message - create the chat and send message together
        await createChatRoom({
          chatRoomId: chatId,
          content,
          id: messageId,
          deploymentId,
          sessionId,
          agentId,
          knowledgeIds,
          images,
          generateTopic: shouldGenerateTopic,
          embedData
        })
      } else {
        // Existing chat - just send the message
        await sendMessageToChat({
          chatRoomId: chatId,
          content,
          id: messageId,
          deploymentId,
          sessionId,
          agentId,
          knowledgeIds,
          images,
          generateTopic: shouldGenerateTopic
        })
      }

      // Return only serializable data - actual message comes via WebSocket
      return {
        chatId,
        messageId,
        content,
        wasNewChatroom: isNewChatroom
      }
    } catch (error: unknown) {
      console.error("❌ [sendEmbedMessage] Error:", error)
      return rejectWithValue(toEmbedRequestFailure(error, "Failed to send message"))
    }
  }
)
